import { newVector, Vector } from '@lessonup/pins-shared';
import React, { useEffect, useRef, useState } from 'react';
import { pinSize } from '../foundations/layout/pinDefault';

export function usePinResizeObserver(pinWrapperRef: React.RefObject<HTMLDivElement | null>, defaultScale: number = 1) {
  const [scale, setScale] = useState<number | null>(null);
  const [pinRendererOffset, setPinRendererOffset] = useState<Vector>(newVector(0, 0));

  const refId = useRef<number | null>(null);
  const lastEntryRef = useRef<ResizeObserverEntry | null>(null);

  const observer = useRef(
    new ResizeObserver((entries) => {
      lastEntryRef.current = entries[0];
      if (refId.current != null) {
        cancelAnimationFrame(refId.current);
      }
      refId.current = requestAnimationFrame(() => {
        const entry = lastEntryRef.current;
        if (!entry) return;
        const offset = pinWrapperRef.current?.getBoundingClientRect();
        const { x, y, width, height } = entry.contentRect; // inner part of the pinWrapper; x and y from padding

        const ratioWidth = width / pinSize.width;
        const ratioHeight = height / pinSize.height;
        const ratio = Math.min(ratioWidth, ratioHeight);

        const topLeftOfPin = {
          x: x + (width - pinSize.width * ratio) / 2,
          y: y + (height - pinSize.height * ratio) / 2,
        };

        if (offset) {
          setPinRendererOffset(newVector(offset.left + topLeftOfPin.x, offset.top + topLeftOfPin.y));
        }

        setScale(ratio);
      });
    })
  );

  useEffect(() => {
    const pin = pinWrapperRef.current;
    if (!pin) {
      return;
    }
    observer.current.observe(pin);
    return () => {
      /* eslint-disable-next-line react-hooks/exhaustive-deps */
      observer.current.disconnect();
      if (refId.current != null) {
        cancelAnimationFrame(refId.current);
        refId.current = null;
      }
    };
  }, [pinWrapperRef]);

  return { scale: scale ?? defaultScale, pinRendererOffset, resizeObserverInitialized: !!scale };
}
